<template>
  <v-container fluid class="ma-4 px-8">
    <v-row align="center">
      <v-col cols="12" class="d-flex justify-center position-relative mx-4">
        <span class="text-h6">
          Admission Report - Active Applications
          <template v-if="search.year">
            ({{ search.year }} - Batch {{ search.batch }})
          </template>
        </span>

        <!-- Viewing Applicants Grouped By:
          {{ form.filter_by === "by_program" ? " Programme" : " Partner" }} -->
        <div
          class="d-flex align-center"
          style="position: absolute; right: 35px"
        >
          <span class="mr-4">Partner</span>
          <v-switch
            v-model="form.filter_by"
            :true-value="'by_program'"
            :false-value="'by_partner'"
            hide-details
            inset
            dense
            class="ma-0 pa-0"
            @change="(panels = []), fetchReport()"
          />
          <span class="">Programme</span>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="2">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
          label="Programme"
          outlined
          dense
          hide-details="auto"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          label="Partner"
          outlined
          dense
          hide-details="auto"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          hide-details="auto"
          label="Batch"
          @change="getPrograms()"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2">
        <v-menu
          ref="menuDateFrom"
          v-model="menuDateFrom"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedStartDate"
              label="Date From"
              prepend-inner-icon="mdi-calendar"
              outlined
              dense
              hide-details="auto"
              readonly
              clearable
              v-bind="attrs"
              v-on="on"
              @click:clear="form.date_from = null"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="form.date_from"
            no-title
            scrollable
            @input="trackDateChange(), (menuDateFrom = false)"
            :max="minDate"
          ></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" md="2">
        <v-menu
          ref="menuDateTo"
          v-model="menuDateTo"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedEndDate"
              label="Date To"
              prepend-inner-icon="mdi-calendar"
              outlined
              dense
              hide-details="auto"
              readonly
              clearable
              v-bind="attrs"
              v-on="on"
              @click:clear="form.date_to = null"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="form.date_to"
            no-title
            scrollable
            :min="form.date_from"
            :max="minDate"
            @input="menuDateTo = false"
          ></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" md="4">
        <v-btn class="accent white--text" @click="fetchReport()">
          Search
        </v-btn>
        <v-btn
          :disabled="
            !reportData?.splitted_report?.length &&
            !reportData?.splitted_report_with_nested?.length &&
            !reportData?.fee_collection
          "
          class="accent white--text ml-2"
          @click="download"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="reportData?.splitted_report?.length">
      <div class="ml-3">
        <span class="text-5xl font-weight-bold accent--text"
          >{{ form.filter_by == "by_program" ? "PROGRAMME" : "PARTNER" }}
          SUMMARY
        </span>
      </div>
      <div class="table-wrapper">
        <table border="1" cellpadding="15" cellspacing="0" class="text-center">
          <thead>
            <tr>
              <th class="px-2" style="min-width: 160px">
                {{ form.filter_by == "by_program" ? "Programme" : "Partner" }}
              </th>
              <th v-for="key in programPartnerSummary" :key="key" class="px-2">
                {{ formatKey(key) }}
              </th>
            </tr>
          </thead>
          <tbody
            v-for="(item, index) in reportData.splitted_report"
            :key="index"
          >
            <!-- Main row (always shown) -->
            <tr>
              <th class="px-2" style="text-align: left; white-space: nowrap">
                <!-- Expand toggle for partner rows that have children -->
                <span
                  v-if="item.children && item.children.length"
                  style="cursor: pointer; margin-right: 4px; user-select: none"
                  @click="togglePartnerExpand(index)"
                >
                  {{ expandedPartners[index] ? "▼" : "▶" }}
                </span>
                <span :style="item.name === 'Total' ? 'font-weight:bold' : ''">
                  {{ item.name }}
                </span>
              </th>
              <td
                v-for="key in programPartnerSummary"
                :key="key"
                style="min-width: 70px"
              >
                {{ item[key] ? formatValue(item[key]) : 0 }}
              </td>
            </tr>

            <!-- Child rows shown when expanded -->
            <template v-if="item.children && expandedPartners[index]">
              <tr
                v-for="(child, ci) in item.children"
                :key="`child-${index}-${ci}`"
                style="background: #f5f5f5"
              >
                <th
                  class="px-2"
                  style="
                    text-align: left;
                    padding-left: 32px;
                    white-space: nowrap;
                    font-weight: normal;
                  "
                >
                  ↳ {{ child.name }}
                </th>
                <td
                  v-for="key in programPartnerSummary"
                  :key="key"
                  style="min-width: 70px"
                >
                  {{ child[key] ? formatValue(child[key]) : 0 }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div class="ml-3 mt-3">
        <span class="text-5xl font-weight-bold accent--text"
          >FEE COLLECTION</span
        >
      </div>
      <div class="table-wrapper">
        <table border="1" cellpadding="15" cellspacing="0" class="text-center">
          <thead>
            <tr>
              <th class="px-2"></th>
              <th
                v-for="(data, index) in reportData.partial_paid"
                :key="index"
                class="px-2"
              >
                {{
                  data.label != "Total" ? data.label : data.label + " Received"
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th class="px-2">Amount Received(₹)</th>
              <td
                v-for="(data, index) in reportData.partial_paid"
                :key="index"
                style="min-width: 70px"
              >
                {{ formatValue(data.paid) }}
              </td>
            </tr>
            <tr>
              <th class="px-2">Number of Students</th>
              <td
                v-for="(data, index) in reportData.partial_paid"
                :key="index"
                style="min-width: 70px"
              >
                {{ data.paid_applications }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="ml-3">
        <span class="text-5xl font-weight-bold accent--text">FEE DUE</span>
      </div>
      <div class="table-wrapper">
        <table border="1" cellpadding="15" cellspacing="0" class="text-center">
          <thead>
            <tr>
              <th class="px-2"></th>
              <th
                v-for="(data, index) in reportData.partial_paid"
                :key="index"
                class="px-2"
              >
                {{ data.label != "Total" ? data.label : data.label + " Due" }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th class="px-2">Amount Due(₹)</th>
              <td
                v-for="(data, index) in reportData.partial_paid"
                :key="index"
                style="min-width: 70px"
              >
                {{ formatValue(data.pending) }}
              </td>
            </tr>
            <tr>
              <th class="px-2">Number of Students</th>
              <td
                v-for="(data, index) in reportData.partial_paid"
                :key="index"
                style="min-width: 70px"
              >
                {{ data.pending_applications }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-row>
    <v-row v-if="reportData?.splitted_report_with_nested?.length">
      <div class="ml-3">
        <span class="text-5xl font-weight-bold accent--text">
          VIEW DETAILS
        </span>
      </div>
      <v-col cols="12">
        <v-expansion-panels v-model="panels" multiple>
          <v-expansion-panel
            v-for="(report, index) in reportData.splitted_report_with_nested"
            :key="index"
          >
            <v-expansion-panel-header>
              <span class="flex pl-0 pa-0">{{ report.name }}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-sheet outlined>
                <!-- Partner has child sub-groups (e.g. VERANDA → own rows + JK SHAH + LOGIC) -->
                <template v-if="report.has_child_partners">
                  <!-- Sub-group tables with expand/collapse -->
                  <div
                    v-for="(subGroup, sgi) in report.sub_groups"
                    :key="`sg-${index}-${sgi}`"
                    class="mb-2"
                  >
                    <!-- Sub-group header row — clickable to expand/collapse -->
                    <div
                      class="d-flex align-center px-3 py-2"
                      style="
                        background: #e8eaf6;
                        cursor: pointer;
                        border: 1px solid #c5cae9;
                        border-radius: 4px;
                        user-select: none;
                      "
                      @click="toggleNestedPartner(index, sgi)"
                    >
                      <span style="margin-right: 8px; font-size: 12px">
                        {{ isNestedPartnerExpanded(index, sgi) ? "▼" : "▶" }}
                      </span>
                      <span class="font-weight-medium">
                        {{ subGroup.partner_name }}
                      </span>
                    </div>

                    <!-- Sub-group table — shown when expanded -->
                    <v-data-table
                      v-if="isNestedPartnerExpanded(index, sgi)"
                      :headers="headers"
                      hide-default-header
                      :items="subGroup.values"
                      hide-default-footer
                      :items-per-page="-1"
                      class="secondary row-height-20"
                      style="border: 1px solid #c5cae9; border-top: none"
                    >
                      <template v-slot:[`item.name`]="{ item }">
                        <div
                          :class="
                            item.name === 'Total'
                              ? 'text-right font-weight-bold'
                              : ''
                          "
                        >
                          {{
                            item.name === "Total" ? item.name + " :" : item.name
                          }}
                        </div>
                      </template>
                      <template v-slot:[`item.amount_paid`]="{ item }">
                        {{ formatValue(item.amount_paid) }}
                      </template>
                      <template v-slot:header="{ props }">
                        <thead>
                          <tr>
                            <th
                              v-for="header in props.headers"
                              :key="header.value"
                              :class="header.class"
                              :style="{
                                width: header.width,
                                minWidth: header.width,
                              }"
                            >
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <span v-bind="attrs" v-on="on">{{
                                    header.text
                                  }}</span>
                                </template>
                                <span>{{ header.tooltip }}</span>
                              </v-tooltip>
                            </th>
                          </tr>
                        </thead>
                      </template>
                    </v-data-table>
                  </div>

                  <!-- Merged grand total table (always visible) -->
                  <v-data-table
                    :headers="headers"
                    :items="[report.values[report.values.length - 1]]"
                    hide-default-footer
                    hide-default-header
                    :items-per-page="-1"
                    class="secondary row-height-20 mt-2"
                    style="border: 1px solid #bdbdbd"
                  >
                    <template v-slot:[`item.name`]="{ item }">
                      <div class="text-right font-weight-bold">
                        {{ item.name }} :
                      </div>
                    </template>
                    <template v-slot:[`item.amount_paid`]="{ item }">
                      {{ formatValue(item.amount_paid) }}
                    </template>
                    <template v-slot:header="{ props }">
                      <thead>
                        <tr>
                          <th
                            v-for="header in props.headers"
                            :key="header.value"
                            :class="header.class"
                            :style="{
                              width: header.width,
                              minWidth: header.width,
                            }"
                          >
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on">{{
                                  header.text
                                }}</span>
                              </template>
                              <span>{{ header.tooltip }}</span>
                            </v-tooltip>
                          </th>
                        </tr>
                      </thead>
                    </template>
                  </v-data-table>
                </template>

                <!-- Normal case — no child partners, single flat table -->
                <v-data-table
                  v-else
                  :headers="headers"
                  :items="report.values"
                  class="secondary row-height-20"
                  hide-default-header
                >
                  <template v-slot:[`item.name`]="{ item }">
                    <div
                      :class="
                        item.name === 'Total'
                          ? 'text-right font-weight-bold'
                          : ''
                      "
                    >
                      {{ item.name === "Total" ? item.name + " :" : item.name }}
                    </div>
                  </template>
                  <template v-slot:[`item.amount_paid`]="{ item }">
                    {{ formatValue(item.amount_paid) }}
                  </template>
                  <template v-slot:header="{ props }">
                    <thead>
                      <tr>
                        <th
                          v-for="header in props.headers"
                          :key="header.value"
                          :class="header.class"
                          :style="{
                            width: header.width,
                            minWidth: header.width,
                          }"
                        >
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <span v-bind="attrs" v-on="on">{{
                                header.text
                              }}</span>
                            </template>
                            <span>{{ header.tooltip }}</span>
                          </v-tooltip>
                        </th>
                      </tr>
                    </thead>
                  </template>
                </v-data-table>
              </v-sheet>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <v-row
      v-if="
        !reportData?.splitted_report?.length &&
        !reportData?.splitted_report_with_nested?.length
      "
      align="center"
      class="px-4"
    >
      <v-col cols="12" class="d-flex justify-center position-relative">
        <span class="text-h6"> No Data Found</span>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { misReportHeaders } from "@/constants/headers";

export default {
  components: {},
  data() {
    return {
      form: {
        program_id: null,
        partner_id: null,
        filter_by: "by_program",
        year_batch: null,
        date_from: null,
        date_to: null,
      },
      search: {
        year: null,
        batch: null,
      },
      filterBy: [
        { text: "Programme", value: "by_program" },
        { text: "Partner", value: "by_partner" },
      ],
      reportData: [],
      programs: [],
      partners: [],
      panels: [],
      headers: [],
      misReportHeaders,
      batches: [],
      menuDateFrom: false,
      menuDateTo: false,
      expandedPartners: {},
      expandedNestedPartners: {},
      programPartnerSummaryBase: [
        // "paid_pre_applications",
        "non_paid_pre_applications",
        "draft",
        "submitted",
        "pending",
        "rejected",
        "re_submitted",
        "provisional",
        "approved",
        "admitted",
        "total",
        "advance_fee_due",
        "advance_fee_paid",
        "balance_fee_due",
        "fully_paid",
        "fully_paid_amount",
        "term_fee_paid",
        "annual_upfront_paid",
        "full_upfront_paid",
        "total_fee_paid",
      ],
      completedAmount: [
        "completed_term_fee_amount",
        "completed_annual_upfront_amount",
        "completed_full_upfront_amount",
        "completed_term_fee_installment_amount",
        "completed_annual_upfront_installment_amount",
        "completed_full_upfront_installment_amount",
        "completed_term_fee_loan_amount",
        "completed_annual_upfront_loan_amount",
        "completed_full_upfront_loan_amount",
        "completed_total_received_amount",
      ],
      completedCount: [
        "completed_term_fee_count",
        "completed_annual_upfront_count",
        "completed_full_upfront_count",
        "completed_term_fee_installment_count",
        "completed_annual_upfront_installment_count",
        "completed_full_upfront_installment_count",
        "completed_term_fee_loan_count",
        "completed_annual_upfront_loan_count",
        "completed_full_upfront_loan_count",
        "completed_total_count",
      ],
      pendingAmount: [
        // "pending_unresolved_fee_type_amount",
        "pending_term_fee_amount",
        "pending_annual_upfront_amount",
        "pending_full_upfront_amount",
        "pending_term_fee_installment_amount",
        "pending_annual_upfront_installment_amount",
        "pending_full_upfront_installment_amount",
        "pending_term_fee_loan_amount",
        "pending_annual_upfront_loan_amount",
        "pending_full_upfront_loan_amount",
        "pending_total_due_amount",
      ],
      pendingCount: [
        // "pending_unresolved_fee_type_count",
        "pending_term_fee_count",
        "pending_annual_upfront_count",
        "pending_full_upfront_count",
        "pending_term_fee_installment_count",
        "pending_annual_upfront_installment_count",
        "pending_full_upfront_installment_count",
        "pending_term_fee_loan_count",
        "pending_annual_upfront_loan_count",
        "pending_full_upfront_loan_count",
        "pending_total_count",
      ],
    };
  },
  watch: {},
  computed: {
    // When a date range is active, Pending and Re-Submitted have no dedicated
    // timestamp field so the backend returns 0 for them. Hide those columns
    // from the summary table to avoid misleading zero counts.
    programPartnerSummary() {
      const hasDateRange = this.form.date_from || this.form.date_to;
      return this.programPartnerSummaryBase.filter((key) => {
        if (hasDateRange && (key === "pending" || key === "re_submitted")) {
          return false;
        }
        return true;
      });
    },
    formattedStartDate() {
      if (!this.form.date_from) return "";
      const [year, month, day] = this.form.date_from.split("-");
      return `${day}-${month}-${year}`;
    },

    formattedEndDate() {
      if (!this.form.date_to) return "";
      const [year, month, day] = this.form.date_to.split("-");
      return `${day}-${month}-${year}`;
    },
    minDate() {
      return new Date().toISOString().substr(0, 10);
    },
  },
  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Admission Report" }]);
    this.form.filter_by = "by_program";
    await this.generateBatchList();
    Promise.all([this.getPrograms(), this.getPartners(), this.fetchReport()]);
  },
  methods: {
    togglePartnerExpand(index) {
      this.$set(this.expandedPartners, index, !this.expandedPartners[index]);
    },
    toggleNestedPartner(panelIndex, subGroupIndex) {
      const key = `${panelIndex}-${subGroupIndex}`;
      this.$set(
        this.expandedNestedPartners,
        key,
        !this.expandedNestedPartners[key],
      );
    },
    isNestedPartnerExpanded(panelIndex, subGroupIndex) {
      const key = `${panelIndex}-${subGroupIndex}`;
      return !!this.expandedNestedPartners[key];
    },
    formatKey(key) {
      const labels = {
        non_paid_pre_applications: "Non Paid Pre Applications",
        draft: "Draft",
        submitted: "Submitted",
        pending: "Pending",
        rejected: "Rejected",
        re_submitted: "Re Submitted",
        provisional: "Provisional",
        approved: "Approved",
        admitted: "Admitted",
        total: "Total",
        fully_paid: "Fully Paid",
        fully_paid_amount: "Fully Paid Amount (₹)",
        term_fee_paid: "Term Fee Paid (₹)",
        annual_upfront_paid: "Annual Upfront Paid (₹)",
        full_upfront_paid: "Full Upfront Paid (₹)",
        total_fee_paid: "Total Fee Paid (₹)",
        advance_fee_paid: "Advance Tuition Fee Paid (₹)",
        advance_fee_due: "Advance Tuition Fee Due (₹)",
        balance_fee_due: "Balance Fee Due (₹)",
      };
      return (
        labels[key] ||
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      );
    },
    formatFeeReportKey(key) {
      // Remove "completed_" from the beginning
      let cleanString = key.replace("completed_", "");
      cleanString = cleanString.replace("pending_", "");
      cleanString = cleanString.replace("_amount", "");

      cleanString = cleanString
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

      return cleanString;
    },
    formatValue(value) {
      if (value === null || value === undefined) return 0;

      const num = parseFloat(value);
      if (isNaN(num)) return value;

      // If it's a whole number, show without decimals
      // If it has real decimal places (e.g. 885000.55), show them
      return num % 1 === 0
        ? num.toLocaleString("en-IN")
        : num.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
    },
    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },
    async getPrograms() {
      try {
        let url = "/programs";
        if (this.form.year_batch) {
          const [year, batch] = this.form.year_batch.split("-");
          url = `/get_batch_programs/${year}/${batch}`;
        }
        const response = await this.$axios.get(url);
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getPartnerName(code) {
      const partner = this.partners.find((partner) => partner.code === code);
      return partner ? partner.name : "";
    },
    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },

    async fetchReport() {
      if (this.form.year_batch) {
        const [year, batch] = this.form.year_batch.split("-");
        this.search.year = year;
        this.search.batch = batch;
        this.form.year = year;
        this.form.batch = batch;
      } else {
        this.search.year = null;
        this.search.batch = null;
        this.form.year = null;
        this.form.batch = null;
      }
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      this.expandedPartners = {};
      this.expandedNestedPartners = {};
      await this.$axios
        .get(`/get_mis_report?${queryString}`)
        .then((res) => {
          this.reportData = res.data.data;
          const hasDateRange = this.form.date_from || this.form.date_to;
          const hiddenWhenDateRange = [
            "Pending",
            "Re Submitted",
            "Re-Submitted",
          ];
          const filterHeaders = (headers) =>
            hasDateRange
              ? headers.filter((h) => !hiddenWhenDateRange.includes(h.text))
              : headers;

          if (this.form.filter_by !== "by_program") {
            this.headers = filterHeaders(
              this.misReportHeaders.filter((h) => h.text !== "Par"),
            );
          } else {
            this.headers = filterHeaders(
              this.misReportHeaders.filter((h) => h.text !== "Pro"),
            );
          }
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    objectToQueryParams(obj) {
      delete obj.year_batch;
      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== "",
        ) // Remove undefined/null values
        .map(
          (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]),
        )
        .join("&");
    },
    async download() {
      this.$axios
        .get("/download_mis_report", {
          params: { ...this.form },
          responseType: "blob",
        })
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Admission Report.csv");
          document.body.appendChild(link);
          link.click();
        });
    },
    async generateBatchList() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
        this.form.year_batch = this.batches[0];
        const [year, batch] = this.form?.year_batch.split("-") || [null, null];
        this.search.year = year;
        this.search.batch = batch;
      } catch (error) {
        console.log(error);
      }
    },
    trackDateChange() {
      if (this.form.date_from > this.form.date_to) {
        this.form.date_to = "";
      }
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  overflow-x: auto;
  width: 100%;
}
/* table th,
table td {
  white-space: nowrap;
} */

/* Sticky first column — plain <table> */
th:first-child,
td:first-child {
  position: sticky;
  left: 0;
  background: white;
  z-index: 2;
}

/* Sticky first column — v-data-table body cells */
::v-deep .v-data-table table td:first-child {
  position: sticky;
  left: 0;
  z-index: 3;
  background: #ffffff; /* solid white — prevents scrolling columns bleeding through */
}

/* Sticky first column — v-data-table header */
::v-deep .v-data-table table thead th:first-child {
  position: sticky;
  left: 0;
  z-index: 4; /* above body cells */
  background: #f5f5f5; /* match Vuetify's default header background */
}

/* Subtle right shadow to indicate sticky column depth */
::v-deep .v-data-table table th:first-child,
::v-deep .v-data-table table td:first-child {
  box-shadow: 2px 2px 4px -2px rgba(0, 0, 0, 0.15);
}
</style>
